import { useState } from 'react';

export default function HeroSection() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const scrollToContact = () => {
    const el = document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleChecklistSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('company_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setStatus('success');
      form.reset();
      return;
    }
    try {
      const response = await fetch('https://readdy.ai/api/form/d9ammtijutvtlj0vtaig', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString(),
      });
      const responseText = await response.text();
      let parsed: any = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = serverMsg?.toLowerCase().includes('spam');
      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        setFormError('');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setFormError('Network error. Please try again.');
    }
  };

  return (
    <section className="relative min-h-[720px] md:min-h-[800px] flex items-center bg-primary-950 overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20bookkeeping%20consultation%20meeting%20in%20modern%20office%20with%20warm%20natural%20lighting%2C%20financial%20documents%20and%20laptop%20on%20table%2C%20two%20business%20people%20reviewing%20reports%20together%2C%20clean%20minimalist%20interior%20design%2C%20soft%20neutral%20warm%20tones%2C%20editorial%20photography%20style&width=1600&height=900&seq=hero-penland-01&orientation=landscape"
          alt="Professional bookkeeping consultation"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/85 to-primary-950/60" />
      </div>

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 py-24 md:py-32">
        <div className="max-w-3xl">
          <p className="text-accent-400 text-sm md:text-base font-semibold tracking-widest uppercase mb-4 md:mb-6">
            Specialized Bookkeeping for the Richmond Area
          </p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-background-50 font-bold leading-[1.1] mb-6">
            Bookkeeping Built for
            <br />
            <span className="text-accent-400">Your Industry</span>
          </h1>
          <p className="text-background-200 text-base md:text-lg leading-relaxed max-w-2xl mb-8">
            We are not generalist bookkeepers. We specialize in restaurants, cafes,
            bars, skilled trades, small businesses, and nonprofits across Richmond,
            Henrico, and Chesterfield. Get proactive insights, compliance peace of mind,
            and real-time financial clarity — so you can focus on running and growing
            your business.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 mb-10">
            <button
              onClick={scrollToContact}
              className="inline-flex items-center justify-center gap-2 bg-accent-500 hover:bg-accent-400 text-primary-950 text-sm md:text-base font-bold px-8 py-4 rounded-full transition-all whitespace-nowrap"
            >
              Schedule Your Free 30-Minute Consultation
              <i className="ri-arrow-right-line" />
            </button>
            <button
              onClick={() => {
                const el = document.querySelector('#checklist-form');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }}
              className="btn-shine btn-shine--light inline-flex items-center justify-center gap-2 bg-transparent border-2 border-background-300/60 text-background-50 hover:bg-background-50/10 text-sm md:text-base font-semibold px-8 py-4 rounded-full transition-all whitespace-nowrap"
            >
              <i className="ri-download-2-line" />
              Download Free Checklist
            </button>
          </div>

          {/* Inline checklist mini-form */}
          <form
            id="checklist-form"
            data-readdy-form
            onSubmit={handleChecklistSubmit}
            className="flex flex-col sm:flex-row gap-3 max-w-lg"
          >
            <input type="text" name="company_alt" className="hp-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
            <input
              type="email"
              name="email"
              placeholder="Enter your email for instant checklist access"
              required
              className="flex-1 bg-background-50/10 border border-background-300/40 rounded-full px-5 py-3 text-sm text-background-50 placeholder:text-background-400 focus:outline-none focus:ring-2 focus:ring-accent-500/50"
            />
            <button
              type="submit"
              className="btn-shine bg-background-50 text-primary-950 hover:bg-accent-100 text-sm font-bold px-6 py-3 rounded-full transition-all whitespace-nowrap shrink-0"
            >
              Get Checklist
            </button>
          </form>
          {status === 'success' && (
            <p className="mt-3 text-accent-300 text-sm font-medium">
              Checklist sent! Check your inbox.
            </p>
          )}
          {status === 'error' && formError && (
            <p className="mt-3 text-red-300 text-sm">{formError}</p>
          )}
        </div>
      </div>
    </section>
  );
}